import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate, useMatch, useLocation, useParams } from 'react-router-dom';
import qs from 'query-string';
import isEqual from 'lodash/isEqual';
import { Hub } from 'aws-amplify/utils';

import mainApi, {
    documentApi,
    useGetDocumentsQuery,
    useGetInvestmentCharacteristicsQuery,
    useGetInvestmentFundQuery,
    useGetInvestmentFundsQuery,
    useGetRolesQuery,
    useGetShareCategoriesQuery,
    useGetShareSubcategoryQuery,
    useGetSubscriptionsQuery,
    useGetTunnelsQuery,
    useGetUsersQuery,
    useSuspendSubscriptionMutation,
    useGetDocumentQuery,
} from 'api/main';
import { userSelectors, tunnelSelectors } from 'reducers';
import { formChangesVerifierSet, ineligibleSet, submitHandlerSet } from 'reducers/tunnel';
import {
    checkAcceptedFileTypes,
    getCompanyDocuments,
    getQueryParameter,
    getQueryParameterString,
    getShareSubcategoryIdFromIRI,
    getSubscriptionData,
    jsonParseSafe,
    saveInStorage,
    validateFileSize,
    getAlertTypeForUser,
    getEntityIdFromIRI,
} from './helpers';
import {
    investmentFunds,
    getProClientOptions,
    RoleNames,
    docTypes,
    fundTypes,
    stepIDs,
    stepStatus,
    subscriptionStatus,
    paymentMethods,
    roleCategories,
    investorClassificationKeys,
    entityKey,
    capitalCallStatus,
    investorRoles,
    environments,
    UserEnrichments,
} from './constants';
import { useMediaQuery } from '@mui/material';
import { notificationCreated } from 'reducers/appNotification';
import { userSchemas } from 'schemas';

export const useSmallScreen = (size = 'sm') => {
    return useMediaQuery(theme => theme.breakpoints.down(size));
};

export const useUserRoleMatchCategory = category => {
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    return useMemo(
        () =>
            Array.isArray(category)
                ? category.includes(currentSubscription?.role?.category.name)
                : currentSubscription?.role?.category.name === category,
        [currentSubscription, category]
    );
};

// check if the current role's name is in the list of roleNames
export const useMatchAtLeastOneRole = (roleNames, forTunnelUse) => {
    const subscription = useSelector(
        forTunnelUse ? userSelectors.getTunnelSubscription : userSelectors.getCurrentSubscription
    );
    const currentRole = useRoleChoice();
    return useMemo(() => {
        if (!Array.isArray(roleNames)) {
            return false;
        }
        return roleNames.includes(subscription?.role?.name || currentRole);
    }, [subscription, roleNames, currentRole]);
};

// check current investment fund
export const useCheckInvestmentFund = investmentFundName => {
    const currentFundName = useSelector(tunnelSelectors.getInvestmentFundName);

    return useMemo(
        () => currentFundName === investmentFundName,
        [currentFundName, investmentFundName]
    );
};

export const useDistributorDocuments = (distributorCompany, userId) => {
    const {
        data: documents = [],
        isFetching,
        refetch,
    } = useGetDocumentsQuery({ belongsTo: userId, pagination: false }, { skip: !userId });

    const otherDocuments = useMemo(
        () => documents?.filter(doc => doc.type === docTypes.OTHER) || [],
        [documents]
    );

    const invoices = useMemo(
        () =>
            documents?.filter(
                doc =>
                    doc.type === docTypes.DISTRIBUTOR_BILL ||
                    doc.type === docTypes.DISTRIBUTOR_BILL_EXCEL
            ) || [],
        [documents]
    );

    const ribDocuments = useMemo(
        () =>
            documents.filter(doc => doc.type === docTypes.RIB).map(doc => ({ ...doc, key: 'rib' })),
        [documents]
    );

    return useMemo(
        () => ({
            documents: getCompanyDocuments(distributorCompany, documents).concat(
                ribDocuments,
                otherDocuments,
                invoices
            ),
            loading: isFetching,
            refetch,
        }),
        [distributorCompany, ribDocuments, otherDocuments, documents, invoices, isFetching, refetch]
    );
};

export const useShareSubCategory = () => {
    const subscription = useSelector(userSelectors.getTunnelSubscription);
    const shareSubcategoryId = useMemo(
        () =>
            subscription?.investmentCharacteristics?.shareSubcategory?.id ||
            getShareSubcategoryIdFromIRI(
                subscription?.investmentCharacteristics?.shareSubcategory || '-1'
            ),
        [subscription]
    );
    const { data: shareSubcategory } = useGetShareSubcategoryQuery(shareSubcategoryId, {
        skip: +shareSubcategoryId <= 0 || isNaN(shareSubcategoryId),
    });

    return shareSubcategory;
};

export const useDiciUrl = (subscription, useMergedUrl = false) => {
    const { type } = useSelector(tunnelSelectors.getCurrentInvestmentFund);
    const isFPCI = type === fundTypes.FPCI;
    const isFPS = type === fundTypes.FPS;
    const shareSubcategoryId = useMemo(() => {
        const subcategory = subscription?.investmentCharacteristics?.shareSubcategory;
        return typeof subcategory === 'string'
            ? getShareSubcategoryIdFromIRI(subcategory)
            : subcategory?.id;
    }, [subscription]);

    const { data: fund } = useGetInvestmentFundQuery(subscription?.investmentFund?.id, {
        skip: !subscription?.investmentFund?.id,
    });

    const { data: shareSubcategory } = useGetShareSubcategoryQuery(shareSubcategoryId, {
        skip: +shareSubcategoryId <= 0 || isNaN(shareSubcategoryId),
    });

    const { data: diciDoc, isLoading: diciLoading } = useGetDocumentQuery(
        shareSubcategory?.dici?.split?.('/')?.pop(),
        { skip: !shareSubcategory?.dici || (useMergedUrl && (isFPCI || isFPS) && fund?.id) }
    );

    return useMemo(() => {
        if (useMergedUrl && (isFPCI || isFPS) && fund?.id) return fund?.mergedDici?.url || '';
        return diciLoading ? '' : diciDoc?.url || '';
    }, [isFPCI, isFPS, fund, useMergedUrl, diciLoading, diciDoc]);
};

export function getStorageValue(key, defaultValue) {
    const saved = localStorage.getItem(key);
    return jsonParseSafe(saved, defaultValue);
}

export const useLocalStorage = (key, defaultValue, useSimpleState = false) => {
    const [value, setValue] = useState(() => {
        return useSimpleState ? defaultValue : getStorageValue(key, defaultValue);
    });

    useEffect(() => {
        if (useSimpleState) return;
        saveInStorage(key, value);
    }, [key, value, useSimpleState, defaultValue]);

    return [value, setValue];
};

export const useLocalStorageFilters = (key, defaultValue, useSimpleState) => {
    const [value, setValue] = useLocalStorage(key, defaultValue, useSimpleState);
    const { enrichWith: defaultEnrichments = [] } = defaultValue;
    const { enrichWith: storageEnrichments = [] } = value;

    useEffect(() => {
        // default enrichments always take precendence if there is a difference
        if (!isEqual([...defaultEnrichments].sort(), [...storageEnrichments].sort())) {
            const newValue = { ...value, enrichWith: defaultEnrichments };
            setValue(newValue);
            saveInStorage(key, newValue);
        }
    }, [defaultEnrichments, key, setValue, storageEnrichments, value]);

    return [value, setValue];
};

// this function is called when the user receives a business error and choses to suspend subscription
export const useConfirmSuspend = () => {
    const dispatch = useDispatch();
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const [suspendSubscription, { isSuccess }] = useSuspendSubscriptionMutation({
        fixedCacheKey: 'suspend-subscription',
    });

    useEffect(() => {
        if (isSuccess) {
            dispatch(ineligibleSet({ open: false }));
            window.localStorage.clear(); // this will log the user out
            window.location.assign('https://www.peqan.fr/');
        }
    }, [isSuccess, dispatch]);

    return useCallback(() => {
        suspendSubscription(currentSubscription?.id);
    }, [suspendSubscription, currentSubscription]);
};

export const useTunnelNavigation = params => {
    const navigate = useNavigate();
    const location = useLocation();

    // react router v6 doesn't provide the current path pattern so we have to get it ourselves
    const currentPathname = useMemo(
        () => '/' + location.pathname.slice(1).split('/')[0],
        [location.pathname]
    );
    const targetPath = useMemo(
        () => (params ? `${currentPathname}/:stepId/:tsId` : `${currentPathname}/:stepId`),
        [currentPathname, params]
    );

    return useCallback(
        (id, queryObject) => {
            if (!id) return;
            const stepPath = generatePath(targetPath, { stepId: id, ...params });
            const query = queryObject ? qs.stringify(queryObject) : '';
            navigate(`${stepPath}${query ? '?' + query : location.search}`, { replace: true }); // do not use the query in the address bar
        },
        [params, navigate, location, targetPath]
    );
};

export const useProClient = currentValue => {
    const { type, name: fundName } = useSelector(tunnelSelectors.getCurrentInvestmentFund);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    return useMemo(() => {
        const isCompany = tunnelSubscription?.role?.name === RoleNames.PM_PROFESSIONNEL;
        const isFPCI = type === fundTypes.FPCI;
        const isFPS = type === fundTypes.FPS;
        const isSLP = type === fundTypes.SLP;
        const isQuad = fundName === investmentFunds.QUAD;
        const isLatour = fundName === investmentFunds.LATOUR;
        const isApax = fundName === investmentFunds.LATOUR;
        const isETI = fundName === investmentFunds.ETI;
        const isCiclad = fundName === investmentFunds.CICLAD;
        const excludeOption16 =
            (isSLP || isQuad || isLatour || isApax || isETI || isCiclad) && isCompany;
        const options = getProClientOptions(excludeOption16);
        return (
            isCompany &&
            (isFPCI || isFPS || isSLP) &&
            options.includes(
                currentValue || tunnelSubscription?.investmentCharacteristics?.investorQualification
            )
        );
    }, [tunnelSubscription, type, fundName, currentValue]);
};

export const useDebounce = (value, delay) => {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);
            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // ... within the delay period. Timeout gets cleared and restarted.
            return () => {
                clearTimeout(handler);
            };
        },
        [value, delay] // Only re-call effect if value or delay changes
    );
    return debouncedValue;
};

export const useSubscriptionDocument = (type, subscription) => {
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    return useMemo(() => {
        const selectedSubscription = subscription || tunnelSubscription;

        return (
            // using Object.values because subscriptionDocuments could be either a list or an object
            Object.values(selectedSubscription?.subscriptionDocuments || [])?.find?.(
                doc => doc?.type === type
            ) || null
        );
    }, [type, subscription, tunnelSubscription]);
};

export const useInvestorPortfolioData = ({
    finishedSubscriptions,
    shareSubcategories,
    capitalAccounts,
    capitalCalls,
    closings,
    navs,
}) => {
    const { data: funds = [] } = useGetInvestmentFundsQuery(
        {},
        { skip: !finishedSubscriptions?.length }
    );
    return useMemo(() => {
        let totalInvestments = 0;
        let totalCapitalCalled = 0;
        let totalValueToDate = 0;
        finishedSubscriptions.forEach(subscription => {
            const { investingSum } = subscription.investmentCharacteristics;
            const { investmentSumCalled, valuation } = getSubscriptionData({
                subscription,
                shareSubcategories,
                capitalAccounts,
                capitalCalls,
                closings,
                navs,
                fund: funds.find(f => f.id === subscription.investmentFund.id),
            });
            totalInvestments += investingSum;
            totalCapitalCalled += investmentSumCalled;
            totalValueToDate += valuation;
        });
        return {
            totalInvestments,
            totalCapitalCalled,
            totalValueToDate,
        };
    }, [
        capitalAccounts,
        finishedSubscriptions,
        shareSubcategories,
        capitalCalls,
        closings,
        funds,
        navs,
    ]);
};

export const useMountedState = () => {
    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return mounted.current;
};

// returns true if investor has signed the subscription form
export const useSubscriptionFormSignatureStatus = subscription => {
    return useMemo(() => {
        if (subscription?.subscriptionStatus === subscriptionStatus.FINISHED) return true;

        const bsStep = subscription?.userTunnelSteps?.find(
            step => step.tunnelStep.step.id === stepIDs.SUBSCRIPTION_FORM_SIGNATURE
        );

        if (
            bsStep?.status === stepStatus.VERIFIED ||
            bsStep?.status === stepStatus.VALIDATED_BY_SERVICER ||
            bsStep?.status === stepStatus.VALIDATED_BY_CIF ||
            bsStep?.status === stepStatus.SIGNED_BY_SGP
        )
            return true;

        return subscription?.withCoSubscriber
            ? bsStep?.status === stepStatus.SIGNED_BY_COSUBSCRIBER
            : bsStep?.status === stepStatus.SIGNED_BY_INVESTOR;
    }, [subscription]);
};

export const useSubscriptionValidationStatus = subscription => {
    return useMemo(() => {
        const bsStep = subscription?.userTunnelSteps?.find(
            step => step.tunnelStep.step.id === stepIDs.SUBSCRIPTION_FORM_SIGNATURE
        );

        return bsStep?.status === stepStatus.VALIDATED_BY_SERVICER;
    }, [subscription]);
};

export const useSubscriptionCanBeVerifiedByDistributor = subscription => {
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const currentUser = useSelector(userSelectors.getInternalUser);
    const isSubscriptionFinished = useMemo(
        () => subscription?.subscriptionStatus === subscriptionStatus.FINISHED,
        [subscription]
    );
    const subscriberHasSigned = useSubscriptionFormSignatureStatus(subscription);

    return useMemo(() => {
        if (subscription) {
            return (
                // subscriber has to had signed the form (co-subscriber as well, if the case)
                subscriberHasSigned &&
                // subscription status is not 'finished'
                !isSubscriptionFinished &&
                // the logged-in user is a distributor admin user
                (currentSubscription?.role?.name === RoleNames.ADMIN_CIF ||
                    currentSubscription?.role?.name === RoleNames.PSI ||
                    currentSubscription?.role?.name === RoleNames.NETWORK_ADMIN) &&
                // and distributor has rights to verify subscriptions
                (subscription?.distributorCompany?.validateSubscriptions ||
                    currentUser?.distributorCompany?.validateSubscriptions) &&
                // Subscription is not validated by CIF
                !subscription.validatedByCIF
            );
        }
        return false;
    }, [
        currentSubscription,
        isSubscriptionFinished,
        subscriberHasSigned,
        subscription,
        currentUser,
    ]);
};
export const useSubscriptionCanBeValidated = (subscription, user, userDocuments = []) => {
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);

    const isSubscriptionFinished = useMemo(
        () => subscription?.subscriptionStatus === subscriptionStatus.FINISHED,
        [subscription]
    );
    const alertType = useMemo(() => getAlertTypeForUser(subscription), [subscription]);
    const subscriptionValidated = useSubscriptionValidationStatus(subscription);
    const subscriberHasSigned = useSubscriptionFormSignatureStatus(subscription);
    const hasNecessaryDocuments = useMemo(
        () =>
            userDocuments?.find(d => d.type === docTypes.ID || d.type === docTypes.SECONDARY_ID) &&
            userDocuments?.find(d => d.type === docTypes.FACTURE || d.type === docTypes.AFFIDAVIT),
        [userDocuments]
    );
    const hasReachedFormSignatureStep = useCallback(
        () =>
            !!subscription?.userTunnelSteps?.find?.(
                ts => ts.step.id === stepIDs.SUBSCRIPTION_FORM_SIGNATURE
            ),
        [subscription?.userTunnelSteps]
    );

    return useMemo(() => {
        if (subscription) {
            return (
                // subscriber has to had signed the form (co-subscriber as well, if the case)
                subscriberHasSigned &&
                // the documents verification has to be completed
                hasNecessaryDocuments &&
                // subscription is not already validated
                !subscriptionValidated &&
                // subscription status is not 'finished'
                !isSubscriptionFinished &&
                // the logged-in user is either Servicer Admin or Signatory
                (currentSubscription?.role?.name === RoleNames.SERVICER_SIGNATORY ||
                    currentSubscription?.role?.name === RoleNames.SERVICER_ADMIN) &&
                // Verified by distributor is not required or is already done
                (!subscription?.distributorCompany?.validateSubscriptions ||
                    subscription.validatedByCIF) &&
                // the subscription has no unsolved alerts
                alertType === '' &&
                // the subscriber has reached the subscription form signature step
                hasReachedFormSignatureStep &&
                // professional investor status validated
                (user?.investorClassification === investorClassificationKeys.PRO
                    ? subscription?.investmentCharacteristics?.professionalClientValidated
                    : true)
            );
        }
        return false;
    }, [
        subscription,
        subscriberHasSigned,
        hasNecessaryDocuments,
        subscriptionValidated,
        isSubscriptionFinished,
        currentSubscription?.role?.name,
        alertType,
        hasReachedFormSignatureStep,
        user?.investorClassification,
    ]);
};

export const useSubscriptionCanBeSignedByProvider = subscription => {
    const subscriptionValidated = useSubscriptionValidationStatus(subscription);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const internalUser = useSelector(userSelectors.getInternalUser);

    const evergreenSubscriptionPaymentConfirmed = useMemo(() => {
        const bsStep = subscription?.userTunnelSteps?.find(
            step => step.tunnelStep.step.id === stepIDs.SUBSCRIPTION_FORM_SIGNATURE
        );

        return bsStep?.status === stepStatus.PAYMENT_CONFIRMED;
    }, [subscription]);

    const SGPsMatch = useMemo(() => {
        return internalUser?.sgp?.id === subscription?.investmentFund?.sgp?.id;
    }, [internalUser, subscription]);

    return useMemo(
        () =>
            currentSubscription?.role?.name === RoleNames.PROVIDER_SIGNATORY &&
            SGPsMatch &&
            (subscription?.investmentFund?.evergreen
                ? evergreenSubscriptionPaymentConfirmed
                : subscriptionValidated),
        [
            currentSubscription,
            SGPsMatch,
            subscriptionValidated,
            evergreenSubscriptionPaymentConfirmed,
            subscription,
        ]
    );
};

export const useHandleGeneralInfoChange = (defaultGeneralState = null) => {
    const [generalInfo, setGeneralInfo] = useState(defaultGeneralState);
    const handleGeneralInfoChange = useCallback((name, value) => {
        if (name === 'dateOfBirth') {
            setGeneralInfo(prevValue => ({
                ...prevValue,
                dateOfBirth: value,
                birthDate: value ? value.format('YYYY-MM-DD') : '',
            }));
        } else {
            setGeneralInfo(prevValue => ({
                ...prevValue,
                [name]: value,
                nationality:
                    name === 'nativeCountry' || name === 'nationality'
                        ? value
                        : prevValue.nationality,
            }));
        }
    }, []);

    return [generalInfo, setGeneralInfo, handleGeneralInfoChange];
};

export const useHandleFileUpload = (
    setCorporateInfo,
    defaultFileSizeError = false,
    defaultFileTypeError = false
) => {
    const [fileSizeError, setFileSizeError] = useState(defaultFileSizeError);
    const [fileTypeError, setFileTypeError] = useState(defaultFileTypeError);

    const handleFileUpload = useCallback(
        e => {
            const file = e.target.files[0];
            checkAcceptedFileTypes(
                file,
                () => {
                    setFileTypeError(false);
                    if (validateFileSize(file)) {
                        setFileSizeError(false);
                        setCorporateInfo(prevState => ({
                            ...prevState,
                            legalRepresentativeDocument: file,
                        }));
                    } else {
                        setFileSizeError(true);
                        setCorporateInfo(prevState => ({
                            ...prevState,
                            legalRepresentativeDocument: null,
                        }));
                    }
                },
                () => {
                    setCorporateInfo(prevState => ({
                        ...prevState,
                        legalRepresentativeDocument: null,
                    }));
                    setFileTypeError(true);
                    setFileSizeError(false);
                }
            );
        },
        [setCorporateInfo]
    );

    return [fileSizeError, fileTypeError, handleFileUpload];
};

export const useGetReferralParameters = () => {
    const location = useLocation();
    return useMemo(() => {
        const label = getQueryParameterString(location.search, 'label');
        const product = getQueryParameter(location.search, 'product');
        const subcategory = getQueryParameter(location.search, 'subcategory');
        const email = getQueryParameterString(location.search, 'email');
        const firstName = getQueryParameterString(location.search, 'first_name');
        const lastName = getQueryParameterString(location.search, 'last_name');
        const title = getQueryParameterString(location.search, 'title');
        return {
            label,
            product,
            subcategory,
            email,
            firstName,
            lastName,
            title,
        };
    }, [location]);
};

export const useTunnelPhases = () => {
    const location = useLocation();
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const currentFund = useSelector(tunnelSelectors.getCurrentInvestmentFund); // fund selected in the tunnel before creating the subscription
    const alerts = useSubscriptionAlerts();

    const usesDirectDebit =
        tunnelSubscription?.bankingStatement?.paymentMethod === paymentMethods.DIRECT_DEBIT;
    const isDistributor =
        tunnelSubscription?.role?.category?.name === roleCategories.ADMIN_DISTRIBUTORS;

    const payload = { investmentFunds: currentFund?.id || undefined };
    if (isDistributor) {
        payload.roleCategory = [tunnelSubscription.role.category.id];
    } else {
        payload.role = tunnelSubscription?.role?.id || undefined;
    }

    const { data: tunnels = [], isLoading: tunnelsLoader } = useGetTunnelsQuery(payload);

    const currentTunnel = useMemo(
        () =>
            tunnelSubscription?.investmentFund?.id
                ? tunnels?.find(
                      t => t.id === getEntityIdFromIRI(tunnelSubscription?.tunnel, entityKey.TUNNEL)
                  ) || tunnels[0]
                : tunnels.find(
                      t =>
                          !!t.investmentFunds.find(
                              f =>
                                  getEntityIdFromIRI(f, entityKey.INVESTMENT_FUND) ===
                                  currentFund.id
                          )
                  ) || tunnels[0],
        [tunnelSubscription, tunnels, currentFund]
    );

    const shouldSeeSurveyResults = useMemo(
        () =>
            currentTunnel?.status === 'default_active' ||
            (!!alerts.find(a => {
                let info = jsonParseSafe(a.additionalInfo);
                return a.alertCode === 121 && info?.wrong_character;
            }) &&
                !tunnelSubscription?.indirectUntransferred),
        [alerts, tunnelSubscription?.indirectUntransferred, currentTunnel]
    );

    const filterTunnelSteps = useCallback(
        ts => {
            if (ts.step.id === stepIDs.SURVEY_RESULTS) {
                return shouldSeeSurveyResults;
            }
            // credit transfer subscriptions do not sign direct debit authorizations
            return usesDirectDebit || ts.step?.id !== stepIDs.DIRECT_DEBIT_AUTHORIZATION_SIGNATURE;
        },
        [shouldSeeSurveyResults, usesDirectDebit]
    );

    return useMemo(() => {
        if (!currentTunnel) return { phases: [], isLoading: tunnelsLoader };
        if (
            location.pathname.includes('onboarding') ||
            (!location.pathname.includes('create-subscription') &&
                !location.pathname.includes('sign-up') &&
                !location.pathname.includes('new-subscription'))
        ) {
            // distributors tunnel
            return {
                phases:
                    tunnels
                        .find(t => t.name === 'admin_distributors')
                        ?.tunnelPhases?.filter(ph => !!ph.tunnelSteps.length) || [],
                isLoading: tunnelsLoader,
            };
        }
        if (tunnelSubscription?.createdBy) {
            // indirect subscriptions (investors)
            return {
                phases:
                    currentTunnel?.tunnelPhases
                        ?.filter(ph => !!ph.tunnelSteps.length)
                        .map(phase => ({
                            ...phase,
                            tunnelSteps: phase.tunnelSteps.filter(filterTunnelSteps),
                        })) || [],
                isLoading: tunnelsLoader,
            };
        }
        // direct subscriptions (investors)
        return {
            phases:
                currentTunnel?.tunnelPhases
                    .filter(ph => !!ph.tunnelSteps.length)
                    .map(phase => ({
                        ...phase,
                        tunnelSteps: [...phase.tunnelSteps.filter(filterTunnelSteps)],
                    })) || [],
            isLoading: tunnelsLoader,
        };
    }, [
        currentTunnel,
        tunnelsLoader,
        location.pathname,
        tunnelSubscription?.createdBy,
        tunnels,
        filterTunnelSteps,
    ]);
};

export const useAllTunnelSteps = () => {
    const { phases } = useTunnelPhases();
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    return useMemo(() => {
        const usesCreditTransfer =
            tunnelSubscription?.bankingStatement?.paymentMethod === paymentMethods.CREDIT_TRANSFER;

        return phases.reduce((acc, ph) => {
            acc = acc.concat(
                ph.tunnelSteps.filter(ts =>
                    usesCreditTransfer
                        ? ts.step.id !== stepIDs.DIRECT_DEBIT_AUTHORIZATION_SIGNATURE
                        : true
                )
            );
            return acc;
        }, []);
    }, [phases, tunnelSubscription]);
};

export const useAllStepIds = () => {
    const tunnelSteps = useAllTunnelSteps();

    return useMemo(() => tunnelSteps.map(ts => ts.step.id), [tunnelSteps]);
};

export const useAllTunnelStepIds = () => {
    const tunnelSteps = useAllTunnelSteps();

    return useMemo(() => tunnelSteps.map(ts => ts.id), [tunnelSteps]);
};

export const useExternalScript = url => {
    let [state, setState] = useState(url ? 'loading' : 'idle');

    useEffect(() => {
        if (!url) {
            setState('idle');
            return;
        }

        let script = document.querySelector(`script[src="${url}"]`);

        const handleScript = e => {
            setState(e.type === 'load' ? 'ready' : 'error');
        };

        if (!script) {
            script = document.createElement('script');
            script.type = 'application/javascript';
            script.src = url;
            script.async = true;
            document.body.appendChild(script);
            script.addEventListener('load', handleScript);
            script.addEventListener('error', handleScript);
        } else {
            setState('ready');
        }

        script.addEventListener('load', handleScript);
        script.addEventListener('error', handleScript);

        return () => {
            script.removeEventListener('load', handleScript);
            script.removeEventListener('error', handleScript);
        };
    }, [url]);

    return state;
};

export const useYousign = ({
    signatureUrl,
    onSuccess,
    onError,
    onCancel,
    containerId,
    singleton,
}) => {
    const status = useExternalScript('https://cdn.yousign.tech/iframe-sdk-1.5.0.min.js');
    const yousign = useRef(null);

    useEffect(() => {
        if (status === 'ready' && signatureUrl) {
            // using recommended implementation adapted to React paradigm
            // refference: https://developers.yousign.com/docs/iframe-advanced
            // the delay is recommended by Yousign tech team
            // because the link could not be yet ready to access earlier than 1 second after it's creation
            setTimeout(() => {
                if (yousign.current && !singleton) {
                    yousign.current.removeMessageListener?.();
                }
                if ((singleton && !yousign.current) || !singleton) {
                    yousign.current = new window.Yousign({
                        signatureLink: signatureUrl,
                        iframeContainerId: containerId,
                        isSandbox: process.env.REACT_APP_ENV !== 'prod',
                        iFrameAttributes: {
                            referrerPolicy: 'strict-origin-when-cross-origin',
                        },
                    });

                    // set signature events
                    yousign.current.onSuccess(onSuccess);
                    yousign.current.onError(onError);
                    yousign.current.onDeclined(onCancel);
                }
            }, 1000);
        }
    }, [signatureUrl, status, onSuccess, onError, onCancel, containerId, singleton]);

    return status;
};

export const useGetDocURLById = (id, defaultState, type = 'image/png') => {
    const [src, setSrc] = useState(defaultState);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        if (id && !defaultState) {
            // get data from server
            setLoading(true);
            documentApi
                .downloadDocumentById(id)
                .then(response => {
                    const bytes = new Uint8Array(response.data);
                    const blob = new Blob([bytes], { type });
                    const url = window.URL.createObjectURL(blob);
                    if (url && mounted) {
                        setSrc(url);
                    }
                    if (mounted) {
                        setLoading(false);
                    }
                })
                .catch(() => {
                    if (mounted) {
                        setLoading(false);
                    }
                });
        } else {
            setSrc(defaultState);
        }

        return () => {
            mounted = false;
        };
    }, [id, type, defaultState]);

    return { src, loading };
};

// returns true only if the ID check step was submitted successfuly
// it's mainly mandatory that a document was uploaded
export const useSubscriberIdCheck = isNotCurrentStep => {
    let { stepId } = useParams();
    const userSteps = useSelector(userSelectors.getUserTunnelSteps);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const idCheckUserStep = useMemo(
        () =>
            isCreatingSubscription
                ? null
                : userSteps.find(us => us.tunnelStep.step.id === stepIDs.ID_CHECK),
        [userSteps, isCreatingSubscription]
    );

    return useMemo(() => {
        if (!tunnelSubscription) return true; // no IN_PROGRESS subscription
        const commonCondition =
            !idCheckUserStep ||
            idCheckUserStep?.status === stepStatus.COMPLETED ||
            idCheckUserStep?.status === stepStatus.VERIFIED ||
            (idCheckUserStep?.status === stepStatus.FAILED &&
                idCheckUserStep?.failedAttemptsNumber > 0); // this would mean a document was uploaded in the Jumio iframe
        if (isNotCurrentStep) {
            return commonCondition;
        } else {
            return +stepId === stepIDs.ID_CHECK ? commonCondition : true;
        }
    }, [tunnelSubscription, idCheckUserStep, isNotCurrentStep, stepId]);
};

// returns true only if the cosubscriber ID check step was submitted successfuly (when applicable)
export const useCosubscriberIdCheck = isNotCurrentStep => {
    let { stepId } = useParams();
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const userSteps = useSelector(userSelectors.getUserTunnelSteps);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');

    const coSubscriberIdCheckUserStep = useMemo(
        () =>
            isCreatingSubscription
                ? null
                : userSteps.find(us => us.tunnelStep.step.id === stepIDs.CO_SUBSCRIBER_ID_CHECK),
        [userSteps, isCreatingSubscription]
    );

    return useMemo(() => {
        if (!tunnelSubscription) return true; // no IN_PROGRESS subscription
        const commonCondition =
            !coSubscriberIdCheckUserStep ||
            coSubscriberIdCheckUserStep?.status === stepStatus.COMPLETED ||
            coSubscriberIdCheckUserStep?.status === stepStatus.VERIFIED ||
            (coSubscriberIdCheckUserStep?.status === stepStatus.FAILED &&
                coSubscriberIdCheckUserStep?.failedAttemptsNumber > 0);
        if (isNotCurrentStep) {
            return tunnelSubscription?.withCoSubscriber ? commonCondition : true;
        } else {
            return +stepId === stepIDs.CO_SUBSCRIBER_ID_CHECK &&
                tunnelSubscription?.withCoSubscriber
                ? commonCondition
                : true;
        }
    }, [tunnelSubscription, coSubscriberIdCheckUserStep, isNotCurrentStep, stepId]);
};

// returns true only if the address check step was submitted successfuly or the subscriber is a company (PM)
export const useAddressCheck = isNotCurrentStep => {
    let { stepId } = useParams();
    stepId = +stepId;
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');
    const userSteps = useSelector(userSelectors.getUserTunnelSteps);

    const addressCheckUserStep = useMemo(
        () =>
            isCreatingSubscription
                ? null
                : userSteps.find(us => us.tunnelStep.step.id === stepIDs.ADDRESS_CHECK),
        [userSteps, isCreatingSubscription]
    );

    return useMemo(() => {
        if (!tunnelSubscription) return true; // no IN_PROGRESS subscription
        const commonCondition =
            !addressCheckUserStep ||
            addressCheckUserStep?.status === stepStatus.COMPLETED ||
            addressCheckUserStep?.status === stepStatus.VERIFIED ||
            (addressCheckUserStep?.status === stepStatus.FAILED &&
                addressCheckUserStep?.failedAttemptsNumber > 0);
        if (isNotCurrentStep) {
            return commonCondition;
        } else {
            return stepId === stepIDs.ADDRESS_CHECK ? commonCondition : true;
        }
    }, [tunnelSubscription, addressCheckUserStep, isNotCurrentStep, stepId]);
};

export const useRoleChoice = () => {
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const roleChoice = useSelector(userSelectors.getRoleChoice);
    return tunnelSubscription?.role?.name || roleChoice;
};

export const useDialog = (initialState = false) => {
    const [open, setOpen] = useState(initialState);
    const onClose = useCallback(() => setOpen(false), []);
    const onOpen = useCallback(() => setOpen(true), []);

    return { open, onClose, onOpen };
};

const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';
const LOGOUT_SYNC_KEY = 'crossTabLogoutTrigger';
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const ACTIVITY_EVENTS = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];

export function useCognitoContextProvider() {
    const dispatch = useDispatch();
    const [context, setContext] = useState({ isSignedIn: false });

    // Only update activity timestamp if user is signed in
    const updateLastActivity = useCallback(() => {
        if (!context.isSignedIn) return;
        localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    }, [context.isSignedIn]);

    // Sign out and sync across tabs
    const triggerLogout = useCallback(() => {
        if (!context.isSignedIn) return;

        if (process.env.REACT_APP_ENV === environments.LOCAL) {
            console.log('[AutoLogout] Logging out (inactivity or auth error)');
        }

        localStorage.setItem(LOGOUT_SYNC_KEY, Date.now().toString());
        setContext(prevState => ({ ...prevState, isSignedIn: false }));
        dispatch(mainApi.endpoints.signOut.initiate(true));
        dispatch(notificationCreated('ERROR_401'));
    }, [context.isSignedIn, dispatch]);

    // Check time difference and log out if expired
    const handleInactivityCheck = useCallback(() => {
        if (!context.isSignedIn) return;

        const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
        if (Date.now() - last >= INACTIVITY_TIMEOUT_MS) {
            triggerLogout();
        }
    }, [context.isSignedIn, triggerLogout]);

    // Handle manual sign out
    const handleSignOut = useCallback(() => {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        setContext(prevState => ({ ...prevState, isSignedIn: false }));
    }, []);

    // Handle Amplify auth events
    const authListener = useCallback(
        ({ payload: { event } }) => {
            switch (event) {
                case 'signedIn':
                case 'tokenRefresh':
                    setContext(prev => ({ ...prev, isSignedIn: true }));
                    updateLastActivity();
                    break;

                case 'signedOut':
                    handleSignOut();
                    break;

                case 'tokenRefresh_failure':
                    if (process.env.REACT_APP_ENV === environments.LOCAL) {
                        console.warn('[AutoLogout] Token refresh failed — logging out');
                    }
                    triggerLogout();
                    break;

                default:
                    break;
            }
        },
        [updateLastActivity, handleSignOut, triggerLogout]
    );

    // Handle logout sync across tabs
    const handleStorage = useCallback(
        e => {
            if (e.key === LOGOUT_SYNC_KEY) {
                if (process.env.REACT_APP_ENV === environments.LOCAL) {
                    console.log('[AutoLogout] Logout triggered in another tab');
                }
                dispatch(mainApi.endpoints.signOut.initiate(true));
            }
        },
        [dispatch]
    );

    // Setup effects: listeners, intervals
    useEffect(() => {
        ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, updateLastActivity));

        window.addEventListener('storage', handleStorage);
        const cancelHubListener = Hub.listen('auth', authListener);

        if (context.isSignedIn) {
            updateLastActivity();
        }

        const interval = setInterval(handleInactivityCheck, 10_000); // Check every 10s

        return () => {
            ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, updateLastActivity));
            window.removeEventListener('storage', handleStorage);
            cancelHubListener();
            clearInterval(interval);
        };
    }, [
        handleInactivityCheck,
        authListener,
        handleStorage,
        updateLastActivity,
        context.isSignedIn,
    ]);

    return context;
}

export const useSubscriptionAlerts = subscription => {
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const currentSubscription = subscription || tunnelSubscription;

    return useMemo(() => {
        if (!currentSubscription) return [];
        return (
            currentSubscription?.userTunnelSteps?.reduce(
                (acc, crt) =>
                    acc.concat(
                        crt.alerts.map(alert => ({
                            ...alert,
                            stepId: crt.tunnelStep.step.id,
                        }))
                    ),
                []
            ) || []
        );
    }, [currentSubscription]);
};

export const useInvestmentsTotalValues = (investmentFund, capitalCalls) => {
    const { data: characteristics = [], isFetching: characteristicsLoader } =
        useGetInvestmentCharacteristicsQuery(
            {
                'shareSubcategory.shareCategory.investmentFund': investmentFund?.id,
                'shareSubcategory.immediatePayment': 0,
                'subscription.subscriptionStatus': subscriptionStatus.FINISHED,
                pagination: false,
            },
            { skip: !investmentFund?.id }
        );

    const totalInvestedAmount = useMemo(
        () => characteristics.reduce((acc, call) => acc + call.investingSum, 0),
        [characteristics]
    );
    const totalCalledPercentage = useMemo(
        () =>
            capitalCalls.reduce((acc, call) => acc + +call.percentage, 0) +
            investmentFund?.firstInstalmentPercentage,
        [capitalCalls, investmentFund]
    );
    const totalCalledAmount = useMemo(
        () =>
            capitalCalls
                .filter(
                    capitalCall =>
                        capitalCall.status === capitalCallStatus.ACTIVATED ||
                        capitalCall.status === capitalCallStatus.COMPLETED
                )
                .reduce((acc, call) => acc + call.issueSum, 0) +
            ((investmentFund?.firstInstalmentPercentage || 0) * totalInvestedAmount) / 100,
        [capitalCalls, investmentFund, totalInvestedAmount]
    );

    return useMemo(
        () => ({
            totalInvestedAmount,
            totalCalledPercentage,
            totalCalledAmount,
            loading: characteristicsLoader,
        }),
        [totalInvestedAmount, totalCalledPercentage, totalCalledAmount, characteristicsLoader]
    );
};

export const useGetTopBarLogoUrl = () => {
    const location = useLocation();
    const currentInvestmentFund = useSelector(tunnelSelectors.getCurrentInvestmentFund);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const internalUser = useSelector(userSelectors.getInternalUser);

    const userIsInvestor = useMatchAtLeastOneRole(investorRoles);
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const userIsDistributor = useUserRoleMatchCategory([
        roleCategories.ADMIN_DISTRIBUTORS,
        roleCategories.DISTRIBUTORS,
    ]);

    const { promoter: myPromoter, isLoading: promoterLoading } = useMyPromoter();

    const { src: distributorLogoUrl, loading: distributorLogoLoading } = useGetDocURLById(
        userIsDistributor ||
            (userIsInvestor &&
                (tunnelSubscription?.distributorCompany || currentSubscription?.distributorCompany))
            ? currentSubscription?.distributorCompany?.companyLogo?.id ||
                  tunnelSubscription?.distributorCompany?.companyLogo?.id
            : null // null value will skip the request
    );
    const { src: providerLogoUrl, loading: providerLogoLoading } = useGetDocURLById(
        userIsProvider ? internalUser?.sgp?.logo?.id : null // null value will skip the request
    );
    const { src: promoterLogoUrl, loading: promoterLogoLoading } = useGetDocURLById(
        userIsPromoter ? myPromoter?.logo?.id : null // null value will skip the request
    );

    const { data: investmentFunds = [], isFetching: fundsLoading } = useGetInvestmentFundsQuery(
        {},
        { skip: !userIsPromoter && !userIsInvestor }
    );

    const fundPromoterLogoURL = useMemo(
        () => investmentFunds.find(f => f.id === currentInvestmentFund?.id)?.promoter?.logo?.url,
        [currentInvestmentFund?.id, investmentFunds]
    );

    const logoUrl = useMemo(() => {
        if (distributorLogoLoading || providerLogoLoading) return null;
        if (userIsDistributor) {
            return distributorLogoUrl;
        } else if (userIsProvider) {
            return providerLogoUrl;
        } else if (userIsPromoter) {
            return promoterLogoUrl;
        } else if (
            userIsInvestor &&
            (location.pathname.includes('/new-subscription') ||
                location.pathname.includes('/sign-up'))
        ) {
            return fundPromoterLogoURL;
        } else {
            return '';
        }
    }, [
        distributorLogoLoading,
        providerLogoLoading,
        userIsDistributor,
        userIsProvider,
        userIsPromoter,
        userIsInvestor,
        location.pathname,
        distributorLogoUrl,
        providerLogoUrl,
        fundPromoterLogoURL,
        promoterLogoUrl,
    ]);

    return useMemo(
        () => ({
            logoUrl: logoUrl || '',
            distributorLogoUrl,
            loading:
                distributorLogoLoading ||
                providerLogoLoading ||
                fundsLoading ||
                promoterLogoLoading ||
                promoterLoading,
        }),
        [
            distributorLogoLoading,
            fundsLoading,
            logoUrl,
            promoterLoading,
            promoterLogoLoading,
            providerLogoLoading,
            distributorLogoUrl,
        ]
    );
};

export const useDistributorShareCategories = fundId => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const userIsDistributor = useUserRoleMatchCategory([
        roleCategories.DISTRIBUTORS,
        roleCategories.ADMIN_DISTRIBUTORS,
    ]);

    const { data: shareCategories = [], isFetching: isLoading } = useGetShareCategoriesQuery(
        { investmentFund: fundId },
        { skip: !fundId || !internalUser }
    );

    const company = useMemo(
        () =>
            internalUser?.distributorCompany ||
            internalUser?.subscriptions?.[0]?.distributorCompany,
        [internalUser]
    );

    const distributorCategories = useMemo(
        () =>
            userIsDistributor
                ? shareCategories.filter(cat =>
                      cat.private
                          ? company?.privateCategoryCommercialization
                          : company?.publicCategoryCommercialization
                  )
                : [],
        [company, shareCategories, userIsDistributor]
    );

    return useMemo(
        () => [distributorCategories, { isLoading }],
        [distributorCategories, isLoading]
    );
};

export const useMyPromoter = () => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const isPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);

    const { data: { userList = [] } = { userList: [] }, isFetching: loader } = useGetUsersQuery(
        userSchemas.getUsers({
            id: internalUser?.id,
            enrichWith: [UserEnrichments.PROMOTER],
        }),
        { skip: !internalUser?.id || !isPromoter }
    );

    return useMemo(
        () => ({ promoter: userList?.[0]?.promoter, isLoading: loader }),
        [loader, userList]
    );
};

// use list of ids for @subcategories
export const useGetShareSubcategorySubscriptions = (subcategories = []) => {
    const { data: roles = [] } = useGetRolesQuery();
    const investorRoles = useMemo(
        () => roles.filter(r => r.category?.name === roleCategories.INVESTORS),
        [roles]
    );
    const { data: { subscriptions = [] } = { subscriptions: [] }, isLoading } =
        useGetSubscriptionsQuery(
            {
                role: investorRoles.map(role => role.id),
                subcategory: subcategories,
                enrichWith: [UserEnrichments.SHARE_SUBCATEGORY],
            },
            { skip: !roles.length || !subcategories.length }
        );

    return { subscriptions, isLoading };
};

/**
 * Hook to detect if the current route is part of a tunnel flow
 * Tunnel flows have their own scroll mechanics and special UI behavior
 *
 * @returns {boolean} true if currently in a tunnel flow, false otherwise
 */
export const useTunnelFlow = () => {
    const location = useLocation();

    return useMemo(() => {
        const { pathname } = location;

        // All tunnel-related paths that have their own scroll mechanics and special behavior
        const tunnelPaths = [
            '/sign-up', // Investor subscription tunnel
            '/onboarding', // Distributor onboarding tunnel
            '/abandonner', // Abandonment page
            '/create-subscription', // Admin creating subscriptions
            '/add-indirect-subscription', // Indirect subscription flow
            '/new-subscription', // New subscription flows
            '/sign-up-success', // Success iframe
            '/sign-up-error', // Error iframe
        ];

        return tunnelPaths.some(path => pathname.includes(path));
    }, [location]);
};

export const useTunnelChangeHandlers = () => {
    const dispatch = useDispatch();

    return useCallback(
        (submitHandler, changesVerifier, customCondition = true) => {
            if (customCondition) {
                dispatch(submitHandlerSet(submitHandler));
                dispatch(formChangesVerifierSet(changesVerifier));
            } else {
                dispatch(submitHandlerSet());
                dispatch(formChangesVerifierSet());
            }

            return () => {
                dispatch(submitHandlerSet());
                dispatch(formChangesVerifierSet());
            };
        },
        [dispatch]
    );
};
